import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

type Option = { label: string; key: string };

type Props = {
  options: Option[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  className?: string;
};

/**
 * Calls tab "Call Type" dropdown (Stitch design). Same inputs as StatusMultiSelect, but ticks
 * are a draft until Apply; Cancel or closing discards them.
 */
export function CallTypeFilter({
  options,
  value,
  onChange,
  placeholder = "Call Type",
  className,
}: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(value);

  const handleOpenChange = (next: boolean) => {
    // Every opening starts from what is currently applied.
    if (next) setDraft(value);
    setOpen(next);
  };

  const toggle = (key: string, checked: boolean) => {
    setDraft((prev) =>
      checked ? [...prev, key] : prev.filter((k) => k !== key),
    );
  };

  const apply = () => {
    // Keep the options' order regardless of click order.
    onChange(options.map((o) => o.key).filter((k) => draft.includes(k)));
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          className={cn(
            "h-8 shrink-0 gap-1.5 rounded-lg px-3 text-xs font-medium",
            className,
          )}
        >
          {value.length > 0 ? `${placeholder} (${value.length})` : placeholder}
          <ChevronDown
            className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 p-0">
        <div className="flex items-center justify-between px-3 pt-3 pb-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Filter by call type
          </span>
          <button
            type="button"
            onClick={() => setDraft([])}
            className="text-[11px] font-medium text-[#2563EB] hover:underline"
          >
            Clear all
          </button>
        </div>

        <div className="px-1.5 pb-1.5">
          {options.map((option) => {
            const checked = draft.includes(option.key);
            return (
              <label
                key={option.key}
                className={cn(
                  "flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-[12px]",
                  checked
                    ? "bg-[#F9FAFB] font-semibold text-foreground"
                    : "text-muted-foreground hover:bg-[#F9FAFB]",
                )}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={(c) => toggle(option.key, c === true)}
                />
                {option.label}
              </label>
            );
          })}
        </div>

        <div className="flex items-center justify-between border-t border-[#E5E7EB] px-3 py-2">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-[12px] font-medium text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
          <Button
            type="button"
            size="sm"
            onClick={apply}
            className="h-7 rounded-md px-3 text-xs"
          >
            Apply ({draft.length})
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
